// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import moment from "moment";

import type { State } from "@store/reducers";
import {
  SectionComponent,
  Button,
  DatePicker,
  TitleSection,
  StatTileGroup,
  View,
} from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import * as selectors from "@modules/reporting/selectors/dashboard/dashboard-index";
import type { FirstcareIndexPageStatistic } from "@modules/reporting/reducer/dashboard/types";
import { DashboardIndexTable } from "@modules/reporting/components";

import "./styles.scss";

type PropsFromState = $Exact<{
  statistic: FirstcareIndexPageStatistic,
}>;

type PropsFromDispatch = $Exact<{
  getFirstCareIndexPageStatistic: typeof actionCreators.GetFirstCareIndexPageStatistic,
}>;

type Props = {
  clientId: number,
  ...PropsFromState,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
};

export function DashboardIndexSection(props: Props) {
  const initialRange = {
    fromDate: moment().subtract(90, "days"),
    toDate: moment(),
  };
  const [state, setState] = useState(initialRange);
  useEffect(() => {
    getFirstCareIndexPageStatistic();
  }, []);

  const statsItems = [
    {
      title: props.t("TOTAL_BENCHMARKS"),
      value: props.statistic.totalAverages,
      footerClassName: "border-bottom-8px-dark-grey",
    },
    {
      title: props.t("BETTER_THAN_NATIONAL_AVERAGE"),
      value: props.statistic.aboveAverageMeasures,
      footerClassName: "border-bottom-8px-mint-d1",
    },
    {
      title: props.t("EQUAL_TO_NATIONAL_AVERAGE"),
      value: props.statistic.equalToAverageMeasures,
    },
    {
      title: props.t("BEHIND_NATIONAL_AVERAGE"),
      value: props.statistic.belowAverageMeasures,
      footerClassName: "border-bottom-8px-negative",
    },
  ];

  function onDateChange(fieldName: string) {
    return function(value: string) {
      setState({
        ...state,
        [fieldName]: moment(value),
      });
    };
  }
  function getFirstCareIndexPageStatistic() {
    props.getFirstCareIndexPageStatistic({
      clientId: props.clientId,
      fromDate: state.fromDate.format("YYYY-MM-DD"),
      toDate: state.toDate.format("YYYY-MM-DD"),
    });
  }
  function onSubmit() {
    getFirstCareIndexPageStatistic();
  }

  function openBenchmarkingPdf() {
    window.open(
      // TODO: remove inline functions
      "/assets/pdf/GoodShape Index Benchmarking FAQ V2 11.10.2021.pdf",
      "_blank",
    );
  }
  return (
    <View gap={8}>
      <View direction="row" justifyContent="space-between">
        <View direction="row" alignItems="center">
          <h4 className="heading">{props.t("BENCHMARKING_INDEX")}</h4>
          <View direction="row">
            <div className="mr-2 d-flex align-items-center">
              <div className="text-color-disabled fs-16 font-weight-bold mr-1 ml-3">
                {props.t("FROM")}
              </div>
              <DatePicker
                value={state.fromDate}
                maxDate={state.toDate}
                hideResetButton
                onChange={onDateChange("fromDate")}
                className="fs-16 font-weight-normal"
              />
            </div>
            <div className="mr-2 d-flex align-items-center">
              <div className="text-color-disabled fs-16 font-weight-bold mr-1">{props.t("TO")}</div>
              <DatePicker
                value={state.toDate}
                minDate={state.fromDate}
                hideResetButton
                onChange={onDateChange("toDate")}
                className="fs-16 font-weight-normal"
              />
            </div>
            <Button materialIcon="replay_circle_filled" className="ml-2" onClick={onSubmit}>
              {props.t("SUBMIT")}
            </Button>
          </View>
        </View>
        <View>
          <Button materialIcon="file_download" onClick={openBenchmarkingPdf}>
            {props.t("EXPORT")}
          </Button>
        </View>
      </View>

      <SectionComponent className="data-dashboard-index dashboard-table">
        <View>
          <StatTileGroup data={statsItems} />
        </View>
      </SectionComponent>
      <DashboardIndexTable
        reportData={props.statistic.absenceLevels}
        title={props.t("ABSENCE_LEVELS")}
        height={200}
      />
      <DashboardIndexTable
        reportData={props.statistic.absenceReasons}
        title={props.t("REPORT_FILTER_ABSENCE_REASON")}
        height={480}
      />
      <DashboardIndexTable
        reportData={props.statistic.ageGroups}
        title={props.t("REPORT_COLUMN_AGE_GROUP")}
        height={320}
      />
      <DashboardIndexTable
        reportData={props.statistic.genders}
        title={props.t("GENDER")}
        height={200}
      />
      <DashboardIndexTable
        reportData={props.statistic.weekdays}
        title={props.t("WEEKDAY")}
        height={360}
      />
      <DashboardIndexTable
        reportData={props.statistic.staffGroups}
        title={props.t("STAFF_GROUPS")}
        height={300}
      />
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  statistic: selectors.getDashboardFirstCareIndexPageStatistic(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getFirstCareIndexPageStatistic: actionCreators.GetFirstCareIndexPageStatistic,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(DashboardIndexSection);
